import { useEffect } from 'react';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type {
  PersistedClient,
  PersistQueryClientOptions,
} from '@tanstack/react-query-persist-client';
import { DASHBOARD_SCHEMA_VERSION } from '@zeroth/shared';
import { validateDashboardData } from './dashboardDataContract';
import { queryClient } from './queryClient';

export const REACT_QUERY_CACHE_KEY = 'zeroth-rq-cache';
export const REACT_QUERY_USER_KEY = 'zeroth-rq-user';
export const APP_CACHE_VERSION = 'page-cache-v3';
export const QUERY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type PersistableQuery = {
  queryKey: readonly unknown[];
  state: {
    status: string;
    data?: unknown;
  };
};

type RestockTablePersistParams = {
  page?: unknown;
  limit?: unknown;
  sortBy?: unknown;
  sortOrder?: unknown;
  filters?: unknown;
};

function getBrowserStorage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.localStorage;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEmptyObject(value: unknown): value is Record<string, never> {
  return isPlainObject(value) && Object.keys(value).length === 0;
}

function isDefaultRestockTableQueryKey(queryKey: readonly unknown[]): boolean {
  const [root, storeId, scope, params] = queryKey;
  if (root !== 'restock' || typeof storeId !== 'string' || scope !== 'table') {
    return false;
  }

  if (!isPlainObject(params)) {
    return false;
  }

  const restockParams = params as RestockTablePersistParams;
  return (
    restockParams.page === 1 &&
    restockParams.limit === 50 &&
    restockParams.sortBy === 'suggestedReorder' &&
    restockParams.sortOrder === 'desc' &&
    isEmptyObject(restockParams.filters)
  );
}

export function isPersistedDashboardQueryKey(queryKey: readonly unknown[]): boolean {
  const [root, scope] = queryKey;

  if (root === 'dashboard') {
    return true;
  }

  if (root === 'analytics') {
    return [
      'revenue-trend',
      'sales-overview',
      'inventory-breakdown',
      'listings-breakdown',
      'top-products',
    ].includes(String(scope));
  }

  if (root === 'reports' && scope === 'summary') {
    return true;
  }

  return isDefaultRestockTableQueryKey(queryKey);
}

export function shouldDehydrateDashboardQuery(query: PersistableQuery): boolean {
  if (query.state.status !== 'success' || !isPersistedDashboardQueryKey(query.queryKey)) {
    return false;
  }

  const [root, , scope, schemaVersion] = query.queryKey;
  return root !== 'dashboard'
    || scope !== 'data'
    || (
      schemaVersion === DASHBOARD_SCHEMA_VERSION
      && validateDashboardData(query.state.data)
    );
}

export function deserializeDashboardCache(cachedString: string): PersistedClient {
  const persisted = JSON.parse(cachedString) as PersistedClient;
  const queries = persisted.clientState?.queries;
  if (!Array.isArray(queries)) return persisted;

  const hasInvalidDashboardData = queries.some((query) => {
    const [root, , scope, schemaVersion] = query.queryKey;
    return root === 'dashboard'
      && scope === 'data'
      && (
        schemaVersion !== DASHBOARD_SCHEMA_VERSION
        || !validateDashboardData(query.state.data)
      );
  });
  if (!hasInvalidDashboardData) return persisted;

  return {
    ...persisted,
    clientState: {
      ...persisted.clientState,
      queries: queries.filter((query) => query.queryKey[0] !== 'dashboard'),
    },
  };
}

export const queryPersister = createSyncStoragePersister({
  storage: getBrowserStorage(),
  key: REACT_QUERY_CACHE_KEY,
  throttleTime: 1000,
  deserialize: deserializeDashboardCache,
});

export const queryClientPersistOptions = {
  persister: queryPersister,
  maxAge: QUERY_CACHE_MAX_AGE_MS,
  buster: APP_CACHE_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: shouldDehydrateDashboardQuery,
  },
} satisfies Omit<PersistQueryClientOptions, 'queryClient'>;

export function clearPersistedCache(storage: Storage | undefined = getBrowserStorage()): void {
  storage?.removeItem(REACT_QUERY_CACHE_KEY);
  queryClient.clear();
}

export function clearPersistedDashboardQueries(
  storage: Storage | undefined = getBrowserStorage(),
): boolean {
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] === 'dashboard',
  });

  if (!storage) return false;

  try {
    const raw = storage.getItem(REACT_QUERY_CACHE_KEY);
    if (!raw) return true;
    const persisted = JSON.parse(raw) as {
      clientState?: { queries?: Array<{ queryKey?: readonly unknown[] }> };
    };
    const queries = persisted.clientState?.queries;
    if (!Array.isArray(queries)) {
      storage.removeItem(REACT_QUERY_CACHE_KEY);
      return true;
    }
    persisted.clientState!.queries = queries.filter((query) => query.queryKey?.[0] !== 'dashboard');
    storage.setItem(REACT_QUERY_CACHE_KEY, JSON.stringify(persisted));
    return true;
  } catch {
    storage.removeItem(REACT_QUERY_CACHE_KEY);
    return true;
  }
}

export function revalidatePersistedDashboardQueries(): Promise<unknown> {
  return queryClient.invalidateQueries({
    predicate: (query) => isPersistedDashboardQueryKey(query.queryKey),
  });
}

export function syncPersistedCacheUserMarker(
  userId: string | null | undefined,
  storage: Storage | undefined = getBrowserStorage()
): boolean {
  if (!storage) {
    return false;
  }

  const nextUserId = userId ?? null;
  const previousUserId = storage.getItem(REACT_QUERY_USER_KEY);
  if (previousUserId === nextUserId) {
    return false;
  }

  clearPersistedCache(storage);

  if (nextUserId) {
    storage.setItem(REACT_QUERY_USER_KEY, nextUserId);
  } else {
    storage.removeItem(REACT_QUERY_USER_KEY);
  }

  return true;
}

export function usePersistedCacheUserIsolation(
  userId: string | null | undefined,
  isAuthResolved: boolean
): void {
  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }

    syncPersistedCacheUserMarker(userId);
  }, [isAuthResolved, userId]);
}
